/**
 * `/geo` — the Geo screen, WP-W10 (Phase 7 SPA, consuming the Task 17 geo API).
 * `web/shapes/geo.md` is the agreed shape, written before this file.
 *
 * The one HR-adjacent surface every employee reaches, not only an administrator:
 * `nav/menu.ts` gates this item on `workforce.view_self` alone, with no role check —
 * a check-in is always the caller's own. Five sections:
 *
 *  - **Check-in** (`CheckinPanel`) — always rendered. Owns no state of its own; this
 *    screen drives `navigator.geolocation` and the mutation, because both need to be
 *    exercised from the same test (the browser API mocked, the request over a
 *    stubbed `fetch`) and a presentational component is what keeps that test from
 *    reaching inside a hook.
 *  - **My check-ins** (`CheckinsListPanel`) — always rendered; re-read once after
 *    every successful check-in so the new row appears without a manual refresh.
 *  - **Team check-ins** (`TeamCheckinsPanel`) — rendered only when the session holds
 *    `workforce.view_department` or `workforce.view_workspace`, the same client-side
 *    honesty gate `LeaveScreen`'s Approvals section uses.
 *  - **Geo policy** (`GeoPolicyPanel`, Task T2-C-web) — the check-in switch, the
 *    trust thresholds and the outside-geofence mode `GET/PUT /geo/policy` (Task
 *    T2-C) shipped with no web caller until now. Rendered only when the session
 *    holds `workforce.manage_policies`.
 *  - **Office locations** (`OfficeLocationsPanel`) — list and create since WP-W10;
 *    Task T2-C-web adds the inline edit and the "Tắt"/"Bật lại" toggle over
 *    `PATCH /geo/office-locations/{id}`. Rendered only when the session holds
 *    `workforce.manage_policies`.
 *
 * ── The consent gate lives here, ahead of the browser prompt ─────────────────
 *
 * `startCheckin` refuses to call `navigator.geolocation.getCurrentPosition` at all
 * unless `consentAck` is already `true` — `web/shapes/geo.md` §2: a screen that asked
 * the browser for a position first and asked about consent after would already have
 * shown the browser's own permission prompt for a check-in nobody had agreed to yet.
 * The request body then carries `consent_ack: true` (a fact this code just checked)
 * and **no `employee_id`** — `api/geo.ts`'s `GeoCheckinRequest` has no member for one.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  GeoCheckin,
  GeoCheckinListResponse,
  GeoCheckinRequest,
  GeoPolicy,
  GeoPolicyUpdateRequest,
  OfficeLocation,
  OfficeLocationCreateRequest,
  OfficeLocationListResponse,
  OfficeLocationUpdateRequest,
} from '../../api/geo';
import {
  GEO_CHECKIN_PATH,
  OFFICE_LOCATIONS_PATH,
  geoCheckinsPath,
  geoPolicyPath,
  officeLocationPath,
} from '../../api/geo';
import {
  browserGeolocation,
  geolocationFailureKind,
  type GeolocationFailureKind,
} from '../../geo/geo';
import { accessHeaders } from '../../access/reason';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import { LastSync } from '../../states/LastSync';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { CheckinPanel } from './CheckinPanel';
import { CheckinsListPanel } from './CheckinsListPanel';
import { TeamCheckinsPanel } from './TeamCheckinsPanel';
import { OfficeLocationsPanel } from './OfficeLocationsPanel';
import { GeoPolicyPanel } from './GeoPolicyPanel';

/** `getCurrentPosition`'s options: a fresh reading, never a cached one, capped at 15s. */
const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15_000,
  maximumAge: 0,
};

export function GeoScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';
  const mayViewTeam =
    (user?.permissions ?? []).includes('workforce.view_department') ||
    (user?.permissions ?? []).includes('workforce.view_workspace');
  const mayManagePolicies = (user?.permissions ?? []).includes('workforce.manage_policies');

  /* ── Check-in ──────────────────────────────────────────────────────────── */
  const [consentAck, setConsentAck] = useState(false);
  const [locating, setLocating] = useState(false);
  const [geoFailure, setGeoFailure] = useState<GeolocationFailureKind | null>(null);
  const [myReloadToken, setMyReloadToken] = useState(0);

  const checkin = useApiMutation<GeoCheckinRequest, GeoCheckin>(GEO_CHECKIN_PATH);

  const startCheckin = (): void => {
    if (!consentAck || locating || checkin.status === 'pending') return;
    setGeoFailure(null);
    checkin.reset();

    const geolocation = browserGeolocation();
    if (geolocation === undefined) {
      setGeoFailure('unsupported');
      return;
    }

    setLocating(true);
    geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        setLocating(false);
        const body: GeoCheckinRequest = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          // `GeolocationCoordinates.accuracy` is a required, non-nullable member of
          // the spec — unlike `altitude`/`heading`/`speed` — so it is always sent.
          accuracy_m: position.coords.accuracy,
          consent_ack: true,
        };
        checkin
          .submit(body)
          .then(() => {
            setMyReloadToken((value) => value + 1);
          })
          .catch(() => {
            /* `submit` resolves rather than rejects; the refusal is already on screen. */
          });
      },
      (error: GeolocationPositionError) => {
        setLocating(false);
        setGeoFailure(geolocationFailureKind(error));
      },
      GEOLOCATION_OPTIONS,
    );
  };

  /* ── My check-ins ──────────────────────────────────────────────────────── */
  const myCheckins = useApiQuery<GeoCheckinListResponse>(geoCheckinsPath(), myReloadToken);

  /* ── Team check-ins (optional; §3 of the shape) ───────────────────────── */
  const [teamEmployeeId, setTeamEmployeeId] = useState<string | null>(null);
  const [teamReason, setTeamReason] = useState('');
  // "chỉ hiện nghi ngờ" (Pillar web, PW3): a plain boolean here, translated into the
  // `trust` query parameter only where `geoCheckinsPath` builds the URL — the state
  // itself carries no verdict, only "is the filter on".
  const [teamSuspectOnly, setTeamSuspectOnly] = useState(false);
  const teamHeaders = accessHeaders(teamReason);
  const teamPath =
    teamEmployeeId === null || teamHeaders === undefined
      ? null
      : geoCheckinsPath(teamEmployeeId, teamSuspectOnly ? 'suspect' : undefined);
  const teamCheckins = useApiQuery<GeoCheckinListResponse>(teamPath, 0, {
    ...(teamHeaders === undefined ? {} : { headers: teamHeaders }),
  });

  /* ── Office locations ──────────────────────────────────────────────────── */
  const [officesToken, setOfficesToken] = useState(0);
  const offices = useApiQuery<OfficeLocationListResponse>(
    mayManagePolicies ? OFFICE_LOCATIONS_PATH : null,
    officesToken,
  );
  const createOffice = useApiMutation<OfficeLocationCreateRequest, OfficeLocation>(
    OFFICE_LOCATIONS_PATH,
  );
  const submitOffice = (body: OfficeLocationCreateRequest): void => {
    createOffice
      .submit(body)
      .then(() => {
        setOfficesToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal stays on screen. */
      });
  };

  // `PATCH /geo/office-locations/{id}` — Task T2-C-web. One hook, called with a
  // per-row `pathOverride`, the same idiom `PermissionGrantsScreen`'s revoke
  // `useApiMutation` uses for a target chosen per press rather than per mount.
  const updateOffice = useApiMutation<OfficeLocationUpdateRequest, OfficeLocation>(
    OFFICE_LOCATIONS_PATH,
    'PATCH',
  );
  const submitOfficeUpdate = (id: string, body: OfficeLocationUpdateRequest): void => {
    updateOffice
      .submit(body, officeLocationPath(id))
      .then(() => {
        setOfficesToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal stays on screen. */
      });
  };

  /* ── Geo policy (Task T2-C-web) ───────────────────────────────────────── */
  const [policyToken, setPolicyToken] = useState(0);
  const policy = useApiQuery<GeoPolicy>(mayManagePolicies ? geoPolicyPath() : null, policyToken);
  const savePolicy = useApiMutation<GeoPolicyUpdateRequest, GeoPolicy>(geoPolicyPath(), 'PUT');
  const submitPolicy = (body: GeoPolicyUpdateRequest): void => {
    savePolicy
      .submit(body)
      .then(() => {
        setPolicyToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects; the refusal stays on screen. */
      });
  };

  return (
    <div className="wp-stack wp-b">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.geo')}</ScreenHeading>
        {/* Both stamps, both `null`, both printed rather than hidden. A check-in is
            a person pressing a button in this browser, not a device heartbeat and
            not an achau sweep, so there is no sync behind this screen to date —
            and saying so is the point of the line. */}
        <LastSync agentSyncAt={null} achauSyncAt={null} timezone={timezone} />
      </div>
      <p className="wp-small wp-muted wp-b-lede">{t('geo.intro')}</p>

      <CheckinPanel
        consentAck={consentAck}
        onConsentChange={setConsentAck}
        locating={locating}
        geoFailure={geoFailure}
        onCheckIn={startCheckin}
        submitPending={checkin.status === 'pending'}
        submitError={checkin.error}
        result={checkin.status === 'ok' ? checkin.data : null}
      />

      <section className="wp-section" aria-labelledby="w10-my-heading" data-testid="w10-my">
        <h2 className="wp-section__title" id="w10-my-heading">
          {t('geo.my.title')}
        </h2>
        <CheckinsListPanel
          status={myCheckins.status}
          checkins={myCheckins.data?.data ?? null}
          error={myCheckins.error}
          onReload={myCheckins.reload}
          subject={t('geo.my.subject')}
          emptyTitle={t('geo.my.empty.title')}
          emptyBody={t('geo.my.empty.body')}
          locale={locale}
          timezone={timezone}
          testPrefix="w10-my"
        />
      </section>

      {mayViewTeam ? (
        <TeamCheckinsPanel
          employeeId={teamEmployeeId}
          onLoadEmployee={setTeamEmployeeId}
          reason={teamReason}
          onOpenReason={setTeamReason}
          status={teamCheckins.status}
          checkins={teamCheckins.data?.data ?? null}
          error={teamCheckins.error}
          onReload={teamCheckins.reload}
          locale={locale}
          timezone={timezone}
          suspectOnly={teamSuspectOnly}
          onSuspectOnlyChange={setTeamSuspectOnly}
        />
      ) : null}

      {mayManagePolicies ? (
        <GeoPolicyPanel
          status={policy.status}
          policy={policy.data ?? null}
          error={policy.error}
          onReload={policy.reload}
          savePending={savePolicy.status === 'pending'}
          saveError={savePolicy.status === 'error' ? savePolicy.error : null}
          saved={savePolicy.status === 'ok' ? savePolicy.data : null}
          onSave={submitPolicy}
          locale={locale}
          timezone={timezone}
        />
      ) : null}

      {mayManagePolicies ? (
        <OfficeLocationsPanel
          status={offices.status}
          offices={offices.data?.data ?? null}
          error={offices.error}
          onReload={offices.reload}
          createPending={createOffice.status === 'pending'}
          createError={createOffice.error}
          created={createOffice.status === 'ok' ? createOffice.data : null}
          onCreate={submitOffice}
          updatePending={updateOffice.status === 'pending'}
          updateError={updateOffice.status === 'error' ? updateOffice.error : null}
          updated={updateOffice.status === 'ok' ? updateOffice.data : null}
          onUpdate={submitOfficeUpdate}
        />
      ) : null}
    </div>
  );
}
